import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import ResultDetail from './ResultDetail';
import { showToast } from '../layout/ToastContainer';

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function formatDateShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function TypeBadge({ type }) {
  const isGeneral = (type || 'issue_report') === 'general_feedback';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
      color: isGeneral ? '#7db8f7' : '#f47b20',
      background: isGeneral ? 'rgba(100,160,255,0.12)' : 'rgba(244,123,32,0.12)',
      padding: '2px 7px', borderRadius: 10, display: 'inline-block', whiteSpace: 'nowrap'
    }}>
      {isGeneral ? 'GENERAL' : 'ISSUE REPORT'}
    </span>
  );
}

const SELECT_STYLE = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border-dim)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)',
  fontSize: 13,
  padding: '6px 10px',
  cursor: 'pointer'
};

export default function ResultsList() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [csvLoading, setCsvLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);

  // Filters
  const [filterType, setFilterType] = useState('all');   // 'all' | 'issue_report' | 'general_feedback'
  const [filterDone, setFilterDone] = useState('all');   // 'all' | 'done' | 'pending'
  const [filterReplied, setFilterReplied] = useState('all'); // 'all' | 'replied' | 'not_replied'
  const [sortDate, setSortDate] = useState('newest');    // 'newest' | 'oldest'

  const load = useCallback(async (archived = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/results${archived ? '?archived=true' : ''}`);
      setResults(data.results);
    } catch {
      showToast('Failed to load results', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(viewArchived); }, [load, viewArchived]);

  useEffect(() => {
    api.get('/settings').then(({ data }) => {
      if (data?.refreshInterval) setRefreshInterval(data.refreshInterval);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => load(viewArchived), refreshInterval * 1000);
    return () => clearInterval(timer);
  }, [load, viewArchived, refreshInterval]);

  async function viewFull(id) {
    try {
      const { data } = await api.get(`/results/${id}`);
      setSelected(data);
    } catch {
      showToast('Failed to load result', 'error');
    }
  }

  function handleDetailUpdate(updatedRecord) {
    if (updatedRecord.archived !== viewArchived) {
      setResults(prev => prev.filter(r => r.id !== updatedRecord.id));
      setSelected(null);
      return;
    }
    setResults(prev => prev.map(r => r.id !== updatedRecord.id ? r : {
      ...r,
      done: updatedRecord.done,
      doneAt: updatedRecord.doneAt,
      archived: updatedRecord.archived,
      archivedAt: updatedRecord.archivedAt,
      emailLog: updatedRecord.emailLog || [],
      imageCount: (updatedRecord.images || []).length
    }));
    setSelected(updatedRecord);
  }

  function promptDelete(id) {
    setSelected(null);
    setConfirmDelete(id);
  }

  async function doDelete(id) {
    setDeleting(id);
    try {
      await api.delete(`/results/${id}`);
      setResults(prev => prev.filter(r => r.id !== id));
      setCheckedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      setConfirmDelete(null);
      showToast('Submission deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    } finally {
      setDeleting(null);
    }
  }

  async function bulkArchive(unarchive = false) {
    if (checkedIds.size === 0) return;
    setArchiving(true);
    try {
      await api.post('/results/bulk-archive', { ids: Array.from(checkedIds), unarchive });
      setResults(prev => prev.filter(r => !checkedIds.has(r.id)));
      const n = checkedIds.size;
      setCheckedIds(new Set());
      showToast(`${n} submission${n !== 1 ? 's' : ''} ${unarchive ? 'unarchived' : 'archived'}`, 'success');
    } catch {
      showToast('Archive action failed', 'error');
    } finally {
      setArchiving(false);
    }
  }

  async function bulkDelete() {
    if (checkedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await api.delete('/results', { data: { ids: Array.from(checkedIds) } });
      const n = checkedIds.size;
      setResults(prev => prev.filter(r => !checkedIds.has(r.id)));
      setCheckedIds(new Set());
      setConfirmBulkDelete(false);
      showToast(`${n} submission${n !== 1 ? 's' : ''} deleted`, 'success');
    } catch {
      showToast('Bulk delete failed', 'error');
    } finally {
      setBulkDeleting(false);
    }
  }

  // Apply search + filters
  let filtered = results.filter(r => {
    if (search && ![r.name, r.email, r.phone, r.ip, r.isp, r.browser, r.os]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))) return false;
    if (filterType !== 'all' && (r.type || 'issue_report') !== filterType) return false;
    if (filterDone === 'done' && !r.done) return false;
    if (filterDone === 'pending' && r.done) return false;
    if (filterReplied === 'replied' && (!r.emailLog || r.emailLog.length === 0)) return false;
    if (filterReplied === 'not_replied' && r.emailLog && r.emailLog.length > 0) return false;
    return true;
  });

  if (sortDate === 'oldest') {
    filtered = [...filtered].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }

  function toggleCheck(id) {
    setCheckedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  function toggleAll() {
    if (checkedIds.size === filtered.length && filtered.length > 0) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filtered.map(r => r.id)));
    }
  }

  async function downloadCSV() {
    if (checkedIds.size === 0) return;
    setCsvLoading(true);
    try {
      const token = localStorage.getItem('speedtest_token');
      const response = await fetch('/api/results/csv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(checkedIds) })
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : 'feedback-export.csv';
      a.click();
      URL.revokeObjectURL(url);
      showToast(`Exported ${checkedIds.size} record${checkedIds.size !== 1 ? 's' : ''}`, 'success');
    } catch {
      showToast('CSV export failed', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  function resetFilters() {
    setSearch('');
    setFilterType('all');
    setFilterDone('all');
    setFilterReplied('all');
    setSortDate('newest');
    setCheckedIds(new Set());
  }

  const hasActiveFilters = search || filterType !== 'all' || filterDone !== 'all' || filterReplied !== 'all' || sortDate !== 'newest';
  const allChecked = filtered.length > 0 && checkedIds.size === filtered.length;
  const someChecked = checkedIds.size > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.05em' }}>
            {viewArchived ? 'Archived Submissions' : 'Submissions'}
          </h1>
          <div style={{ color: '#9aa3b0', fontSize: 13, marginTop: 4 }}>
            {results.length} total record{results.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
            Viewer Feedback &nbsp;&uarr;
          </a>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setViewArchived(v => !v); setCheckedIds(new Set()); setSearch(''); }}
              style={{ color: viewArchived ? 'var(--accent-orange)' : '#d0d8e4', borderColor: viewArchived ? 'var(--accent-orange)' : '#3e4a5a' }}>
              {viewArchived ? '← Active' : '⊘ Archived'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => load(viewArchived)} disabled={loading}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              {loading ? <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : '↻ Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="input-field"
          placeholder="Search by name, email, IP, browser..."
          value={search}
          onChange={e => { setSearch(e.target.value); setCheckedIds(new Set()); }}
          style={{ maxWidth: 300 }}
        />
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setCheckedIds(new Set()); }} style={SELECT_STYLE}>
          <option value="all">All Types</option>
          <option value="issue_report">Issue Reports</option>
          <option value="general_feedback">General Feedback</option>
        </select>
        <select value={filterDone} onChange={e => { setFilterDone(e.target.value); setCheckedIds(new Set()); }} style={SELECT_STYLE}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="done">Done</option>
        </select>
        <select value={filterReplied} onChange={e => { setFilterReplied(e.target.value); setCheckedIds(new Set()); }} style={SELECT_STYLE}>
          <option value="all">All Replied</option>
          <option value="replied">Replied</option>
          <option value="not_replied">Not Replied</option>
        </select>
        <select value={sortDate} onChange={e => setSortDate(e.target.value)} style={SELECT_STYLE}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
        {hasActiveFilters && (
          <button className="btn btn-ghost btn-sm" onClick={resetFilters}
            style={{ color: '#9aa3b0', borderColor: '#3e4a5a', fontSize: 12 }}>
            Clear Filters
          </button>
        )}
      </div>

      {/* Bulk Actions */}
      {someChecked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{checkedIds.size} selected</span>
          {viewArchived ? (
            <button className="btn btn-ghost btn-sm" onClick={() => bulkArchive(true)} disabled={archiving}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              {archiving ? 'Unarchiving...' : '↩ Unarchive'}
            </button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={() => bulkArchive(false)} disabled={archiving}
              style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
              {archiving ? 'Archiving...' : '⊘ Archive'}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={csvLoading}
            style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>
            {csvLoading ? 'Exporting...' : '↓ CSV'}
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => setConfirmBulkDelete(true)}>
            Delete
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setCheckedIds(new Set())}
            style={{ color: '#9aa3b0', borderColor: '#3e4a5a' }}>Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
            {search || hasActiveFilters ? 'No submissions match your filters.' : viewArchived ? 'No archived submissions.' : 'No submissions yet.'}
          </div>
        ) : (
          <table className="data-table" style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={{ width: 36, textAlign: 'center' }}>
                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                    style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                </th>
                <th>Date</th>
                <th>Type</th>
                <th>Name</th>
                <th>Email</th>
                <th style={{ textAlign: 'right' }}>Down↓</th>
                <th style={{ textAlign: 'right' }}>Up↑</th>
                <th style={{ textAlign: 'right' }}>Ping</th>
                <th>Browser</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const isGeneral = (r.type || 'issue_report') === 'general_feedback';
                return (
                  <tr key={r.id} style={{ opacity: r.done ? 0.7 : 1 }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={checkedIds.has(r.id)} onChange={() => toggleCheck(r.id)}
                        style={{ cursor: 'pointer', accentColor: 'var(--accent-orange)' }} />
                    </td>
                    <td style={{ color: '#b8c0cc', whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                    <td><TypeBadge type={r.type} /></td>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td style={{ color: '#c8d0dc' }}>{r.email}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: isGeneral ? '#6e7789' : 'var(--accent-orange)', fontWeight: 600 }}>
                      {isGeneral ? <span style={{ color: 'var(--text-muted)' }}>—</span> : <>{r.download.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span></>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: isGeneral ? '#6e7789' : 'var(--accent-orange)', fontWeight: 600 }}>
                      {isGeneral ? <span style={{ color: 'var(--text-muted)' }}>—</span> : <>{r.upload.toFixed(1)} <span style={{ color: '#9aa3b0', fontWeight: 400 }}>Mbps</span></>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>
                      {isGeneral ? <span style={{ color: 'var(--text-muted)' }}>—</span> : <>{r.ping.toFixed(0)} <span style={{ color: '#9aa3b0' }}>ms</span></>}
                    </td>
                    <td style={{ color: '#c8d0dc' }}>{r.browser}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {r.done && (
                          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: '#00c88c', background: 'rgba(0,200,140,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                            ✓ DONE
                          </span>
                        )}
                        {r.emailLog && r.emailLog.length > 0 && (
                          <span style={{ fontSize: 10, letterSpacing: '0.04em', color: '#7db8f7', background: 'rgba(100,160,255,0.12)', padding: '2px 7px', borderRadius: 10, display: 'inline-block' }}>
                            ✉ {formatDateShort(r.emailLog[r.emailLog.length - 1].sentAt)}
                          </span>
                        )}
                        {(!r.done && (!r.emailLog || r.emailLog.length === 0)) && (
                          <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                        )}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => viewFull(r.id)}
                          style={{ color: '#d0d8e4', borderColor: '#3e4a5a' }}>View</button>
                        <button className="btn btn-danger btn-sm" onClick={() => promptDelete(r.id)} disabled={deleting === r.id}>
                          {deleting === r.id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <ResultDetail record={selected} onClose={() => setSelected(null)} onDelete={promptDelete} onUpdate={handleDetailUpdate} />
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Confirm Delete</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Are you sure you want to permanently delete this submission? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => doDelete(confirmDelete)} disabled={deleting === confirmDelete}>
                {deleting === confirmDelete ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmBulkDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmBulkDelete(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Confirm Bulk Delete</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                Permanently delete <strong style={{ color: 'var(--text-primary)' }}>{checkedIds.size} submission{checkedIds.size !== 1 ? 's' : ''}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmBulkDelete(false)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={bulkDelete} disabled={bulkDeleting}>
                {bulkDeleting ? 'Deleting...' : `Delete ${checkedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
